import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  CurrencyDollarIcon,
  CogIcon,
  BanknotesIcon,
  ArrowsRightLeftIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { useFinance } from '../../context/FinanceContext';
import { useAuth } from '../../context/AuthContext';
import OrganizationSwitcher from '../common/OrganizationSwitcher';

interface SidebarProps {
  isMobile: boolean;
  isCollapsed: boolean;
  toggleSidebar: () => void;
  toggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isMobile, isCollapsed, toggleSidebar, toggleCollapse }) => {
  const location = useLocation();
  const { statusReviewTransactions, loadReviewTransactions } = useFinance();
  const { user, logout } = useAuth();
  
  // Load review transactions when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        await loadReviewTransactions();
      } catch (error) {
        console.error('Failed to load review transactions for sidebar:', error);
      }
    };
    loadData();
  }, [loadReviewTransactions]);

  const navItems = [
    { name: 'Dashboard', path: '/', icon: HomeIcon },
    { name: 'Transactions', path: '/transactions', icon: ArrowsRightLeftIcon },
    { name: 'Accounts', path: '/accounts', icon: BanknotesIcon },
    { name: 'Transfers', path: '/transfers', icon: ArrowsRightLeftIcon },
    { name: 'Rules', path: '/rules', icon: DocumentTextIcon },
    { name: 'Reports', path: '/reports', icon: CurrencyDollarIcon },
    { name: 'Settings', path: '/settings', icon: CogIcon },
  ];

  return (
    <div className={`${isMobile ? 'sidebar-mobile' : ''} h-full flex flex-col`}>
      <div className="sidebar-logo">
        <Link to="/" className="flex items-center">
          <span className="text-2xl font-bold text-primary">{isCollapsed ? 'FS' : 'FinStack'}</span>
        </Link>
        {!isMobile && (
          <button
            onClick={toggleCollapse}
            className="ml-auto p-1 hover:bg-gray-100 rounded-md transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRightIcon className="h-5 w-5 text-gray-600" />
            ) : (
              <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
            )}
          </button>
        )}
      </div>
      <nav className="sidebar-nav">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          
          return (
            <Link
              key={item.name}
              to={item.path}
              className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
              onClick={isMobile ? toggleSidebar : undefined}
              title={isCollapsed ? item.name : ''}
            >
              <Icon className={`h-5 w-5 ${isActive ? 'text-primary' : ''}`} />
              {!isCollapsed && (
                <div className="flex items-center">
                  {item.name}
                  {item.name === 'Transactions' && statusReviewTransactions && statusReviewTransactions.length > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                      {statusReviewTransactions.length}
                    </span>
                  )}
                </div>
              )}
              {isCollapsed && item.name === 'Transactions' && statusReviewTransactions && statusReviewTransactions.length > 0 && (
                <span className="absolute left-8 -top-1 bg-red-500 text-white text-xs font-medium px-1.5 py-0.5 rounded-full">
                  {statusReviewTransactions.length}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      <div className="sidebar-footer">
        {/* User Information Section */}
        {user && !isCollapsed && (
          <div className="mb-3 pb-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <span className="text-sm text-gray-700 truncate">
                  {user.email}
                </span>
              </div>
              <button
                onClick={logout}
                className="btn btn-secondary p-1 rounded flex-shrink-0 ml-2"
                title="Sign out"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        
        {/* Collapsed sidebar - just show sign out button */}
        {user && isCollapsed && (
          <div className="mb-2 flex justify-center">
            <button
              onClick={logout}
              className="btn btn-secondary p-1 rounded"
              title="Sign out"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {/* Organization Switcher */}
        <OrganizationSwitcher isCollapsed={isCollapsed} />
      </div>
    </div>
  );
};

export default Sidebar;
